import { useMemo, useState } from 'react';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import {
  useScopedServiceRequestsFormState,
  UseScopedServiceRequestsFormStateReturn,
} from 'src/pages/ServiceRequests/CustomHooks/useScopedServiceRequestsFormState';

/* Property SR list-state = the shared scoped form-state (search + resolved
 * toggle + selection + group expansion + download overlay) plus the
 * Property-only bulk-action modals (decline / assign / grouping) and the
 * derived list of selected requests those modals operate on. */
export interface UsePropertyServiceRequestsFormStateArgs {
  serviceRequests: IServiceRequest[];
}

export interface UsePropertyServiceRequestsFormStateReturn
  extends UseScopedServiceRequestsFormStateReturn {
  selectedServiceRequests: IServiceRequest[];

  isDeclineModalOpen: boolean;
  openDeclineModal: () => void;
  closeDeclineModal: () => void;

  isAssignOverlayOpen: boolean;
  openAssignOverlay: () => void;
  closeAssignOverlay: () => void;

  isGroupingModalOpen: boolean;
  openGroupingModal: () => void;
  closeGroupingModal: () => void;
}

export const usePropertyServiceRequestsFormState = ({
  serviceRequests,
}: UsePropertyServiceRequestsFormStateArgs): UsePropertyServiceRequestsFormStateReturn => {
  const scopedFormState: UseScopedServiceRequestsFormStateReturn =
    useScopedServiceRequestsFormState({ serviceRequests });

  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState<boolean>(false);
  const [isAssignOverlayOpen, setIsAssignOverlayOpen] =
    useState<boolean>(false);
  const [isGroupingModalOpen, setIsGroupingModalOpen] =
    useState<boolean>(false);

  const selectedServiceRequests: IServiceRequest[] = useMemo(() => {
    const idSet: Set<string> = new Set(scopedFormState.selectedIds);
    return serviceRequests.filter((sr: IServiceRequest) => idSet.has(sr.id));
  }, [serviceRequests, scopedFormState.selectedIds]);

  const openDeclineModal = (): void => {
    setIsDeclineModalOpen(true);
  };

  const closeDeclineModal = (): void => {
    setIsDeclineModalOpen(false);
  };

  const openAssignOverlay = (): void => {
    setIsAssignOverlayOpen(true);
  };

  const closeAssignOverlay = (): void => {
    setIsAssignOverlayOpen(false);
  };

  const openGroupingModal = (): void => {
    setIsGroupingModalOpen(true);
  };

  const closeGroupingModal = (): void => {
    setIsGroupingModalOpen(false);
  };

  const reset = (): void => {
    scopedFormState.reset();
    setIsDeclineModalOpen(false);
    setIsAssignOverlayOpen(false);
    setIsGroupingModalOpen(false);
  };

  return {
    ...scopedFormState,

    selectedServiceRequests,

    isDeclineModalOpen,
    openDeclineModal,
    closeDeclineModal,

    isAssignOverlayOpen,
    openAssignOverlay,
    closeAssignOverlay,

    isGroupingModalOpen,
    openGroupingModal,
    closeGroupingModal,

    reset,
  };
};